export const DEFAULT_RENDER_OPERATION_TIMEOUT_MS = 45_000;
export const DEFAULT_RENDER_STALL_TIMEOUT_MS = 60_000;

export interface RenderOperationOptions {
  signal?: AbortSignal;
  label: string;
  timeoutMs?: number;
}

export function getRenderAbortError(
  signal?: AbortSignal,
  fallbackMessage = 'Video export canceled.'
): Error {
  const reason = signal?.reason;
  if (reason instanceof Error) {
    return reason;
  }
  if (typeof reason === 'string' && reason.trim()) {
    return new Error(reason);
  }
  return new Error(fallbackMessage);
}

export function throwIfRenderAborted(signal?: AbortSignal): void {
  if (signal?.aborted) {
    throw getRenderAbortError(signal);
  }
}

export async function waitForRenderOperation<T>(
  operation: Promise<T>,
  options: RenderOperationOptions
): Promise<T> {
  throwIfRenderAborted(options.signal);

  const timeoutMs = options.timeoutMs ?? DEFAULT_RENDER_OPERATION_TIMEOUT_MS;
  let cleanupControl = () => {};
  const control = new Promise<never>((_, reject) => {
    const timeoutId = window.setTimeout(
      () => reject(new Error(`${options.label} stopped responding.`)),
      timeoutMs
    );
    const onAbort = () => reject(getRenderAbortError(options.signal));
    options.signal?.addEventListener('abort', onAbort, { once: true });
    cleanupControl = () => {
      window.clearTimeout(timeoutId);
      options.signal?.removeEventListener('abort', onAbort);
    };
  });

  try {
    return await Promise.race([operation, control]);
  } finally {
    cleanupControl();
  }
}

export async function yieldToRenderControls(signal?: AbortSignal): Promise<void> {
  throwIfRenderAborted(signal);
  await new Promise<void>((resolve, reject) => {
    let settled = false;
    let timeoutId: number | null = null;
    const cleanup = () => {
      signal?.removeEventListener('abort', onAbort);
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
        timeoutId = null;
      }
    };
    const finishResolve = () => {
      if (settled) {
        return;
      }
      settled = true;
      cleanup();
      resolve();
    };
    const finishReject = () => {
      if (settled) {
        return;
      }
      settled = true;
      cleanup();
      reject(getRenderAbortError(signal));
    };
    const onAbort = () => finishReject();
    signal?.addEventListener('abort', onAbort, { once: true });
    timeoutId = window.setTimeout(finishResolve, 0);
  });
}

export class RenderStallWatchdog {
  private timeoutId: number | null = null;
  private stopped = false;

  constructor(
    private readonly onStall: () => void,
    private readonly timeoutMs = DEFAULT_RENDER_STALL_TIMEOUT_MS
  ) {
    this.touch();
  }

  touch(): void {
    if (this.stopped) {
      return;
    }
    if (this.timeoutId !== null) {
      window.clearTimeout(this.timeoutId);
    }
    this.timeoutId = window.setTimeout(() => {
      this.timeoutId = null;
      if (!this.stopped) {
        this.onStall();
      }
    }, this.timeoutMs);
  }

  stop(): void {
    this.stopped = true;
    if (this.timeoutId !== null) {
      window.clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
  }
}
